import {
	apiPost,
	type FetchResponse,
	getApiUrl,
	type PartnerOfferResponseDto,
} from "Infrastructure/Api/Api";
import { processResponse } from "Infrastructure/Api/ApiUtils";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { invalidateOrderDetailBundle } from "Order/Api/invalidateOrderQueries";
import { orderKeys } from "Order/Api/orderKeys";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

type AllocationFlowRequestDto = {
	forceReallocation?: boolean;
};

export type AllocationFlowResponseDto = {
	status: "created" | "already_allocated";
	partnerId?: number | null;
	offers: PartnerOfferResponseDto[];
};

type StartAllocationFlowInput = AllocationFlowRequestDto & {
	orderId: number;
};

type StartAllocationFlowFetchResponse = FetchResponse<
	AllocationFlowResponseDto,
	200 | 201
>;

const orderMutationsResource = Resources.base.order.mutations;

function postAllocationFlow({
	orderId,
	...data
}: StartAllocationFlowInput): Promise<StartAllocationFlowFetchResponse> {
	return apiPost<StartAllocationFlowFetchResponse, AllocationFlowRequestDto>(
		`${getApiUrl()}/partners/calendar/orders/${orderId}/allocation-flow`,
		data,
		new Headers(),
	);
}

export function useStartAllocationFlow(orderId: number) {
	const { t } = useResource();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: processResponse<
			StartAllocationFlowInput,
			StartAllocationFlowFetchResponse
		>(postAllocationFlow),
		onError: (error) => {
			toast.error(error.message);
			console.error(error);
		},
		onSuccess: async (response) => {
			if (response.status === "already_allocated") {
				return;
			}

			toast.success(t(orderMutationsResource.orderAllocationFlowSuccess));
			await Promise.all([
				invalidateOrderDetailBundle(queryClient, orderId),
				queryClient.invalidateQueries({ queryKey: orderKeys.listAll }),
				queryClient.invalidateQueries({ queryKey: ["partners", "calendar"] }),
				queryClient.invalidateQueries({
					queryKey: ["partners", "calendar", "offers"],
				}),
			]);
		},
	});
}
